import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useThemeContext } from "@/shared/providers/ThemeProvider";
import { useAuth } from "@/shared/providers/AuthProvider";

/**
 * The user's chosen appearance:
 *   • "light"  — always light theme regardless of OS preference
 *   • "dark"   — always dark theme regardless of OS preference
 *   • "system" — follow the OS / browser `prefers-color-scheme`
 *
 * `system` is the default for first-time users so the app respects
 * whatever the rest of the user's OS is doing without surprising them.
 */
export type Appearance = "light" | "dark" | "system";

/** The actually-applied theme after resolving "system". Always concrete. */
export type ResolvedAppearance = "light" | "dark";

/**
 * Dark-theme CSS variable values applied as INLINE styles on `<html>`.
 * We can't rely solely on the `.dark { ... }` ruleset in globals.css
 * because the tenant `ThemeProvider.applyTheme` writes light-theme
 * variables as inline styles on `<html>`, and inline styles beat class
 * selectors in CSS specificity. Re-applying the dark values inline at
 * the same scope guarantees they win in dark mode; removing them
 * restores the tenant's light values for light mode.
 *
 * Anchored on the Figma-specified `#1A1A1A` page background (neutral
 * gray, not navy). Surfaces step up in 8 % luminance increments
 * (#1A1A1A → #262626 → #303030).
 *
 * NOT overridden in dark mode (intentionally inherits the light /
 * tenant value — the navbar is part of the brand chrome and stays
 * consistent across themes):
 *   • `--color-header`                 (navbar background — navy)
 *   • `--color-on-primary`             (white icons / text on navbar)
 *   • `--color-on-secondary`           (white text on secondary)
 *   • `--color-sidebar-header`         (sidebar sub-header)
 *   • `--color-sidebar-header-border`
 *
 * Keep this in sync with the `.dark` ruleset in `src/styles/globals.css`.
 */
const DARK_TOKENS: Record<string, string> = {
  "--color-primary":              "51 153 255",   // #3399FF — Figma Dark Mode Link/Primary Blue
  "--color-primary-btn":          "0 121 194",    // #0079C2 — Figma Brand Primary Blue; text-on-primary (#FFFFFF) vs #0079C2 ≈ 4.88:1, meets WCAG 2.1 AA (≥4.5:1 for normal text)
  "--color-primary-light":        "26 26 26",     // #1A1A1A — Elevated Background on dark
  "--color-secondary":            "1 169 181",    // #01A9B5 — Figma Brand Secondary Teal
  "--color-background":           "18 18 18",     // #121212 — Figma Main Background
  "--color-surface":              "18 18 18",     // #121212 — intentionally same as background: Figma dark uses flat surface (no tinted lift)
  "--color-surface-alt":          "26 26 26",     // #1A1A1A — Figma Elevated Background (hover/selected)
  "--color-surface-secondary":    "42 42 42",     // #2A2A2A — detail page / elevated container background
  "--color-form-label":           "229 229 229",  // #E5E5E5 — form field label text in dark mode (matches --color-text)
  "--color-text":                 "229 229 229",  // #E5E5E5 — Figma Text Primary
  "--color-text-muted":           "160 160 160",  // #A0A0A0 — Figma Text Secondary
  "--color-text-strong":          "229 229 229",  // #E5E5E5 — intentionally same as text: Figma dark spec uses single text value
  "--color-text-disabled":        "112 112 112",  // #707070 — Figma Text Muted
  "--color-text-subtle":          "112 112 112",  // #707070 — intentionally same as text-disabled: Figma dark spec uses single muted value
  "--color-border":               "58 58 58",     // #3A3A3A — Figma table row border (visible on #121212)
  "--color-border-subtle":        "42 42 42",     // #2A2A2A — Figma Subtle Border
  "--color-border-strong":        "64 64 64",     // #404040 — Figma Medium Border
  "--color-success":              "34 197 94",    // #22C55E — WCAG AA compliant (~5.8:1 on #121212)
  "--color-warning":              "245 158 11",   // #F59E0B — close to Figma Orange 1
  "--color-error":                "239 68 68",    // #EF4444 — Figma Red 1
  "--color-info":                 "0 121 194",    // #0079C2 — Figma Brand Primary Blue
  "--color-on-semantic":          "255 255 255",  // #FFFFFF — white text on semantic chips (success/warning/error bg-*/10 tints)
  "--color-online":               "34 197 94",    // #22C55E — WCAG AA compliant (matches --color-success)
  "--color-online-bg":            "26 26 26",     // #1A1A1A — Elevated Background
  "--color-ring":                 "0 121 194",    // #0079C2 — Figma Brand Primary Blue
  "--color-accordion-arrow":      "160 160 160",  // #A0A0A0 — Figma Text Secondary
  "--color-reset-button":         "229 229 229",  // #E5E5E5 — Figma Text Primary
  "--color-arrow-icon":           "160 160 160",  // #A0A0A0 — Figma Text Secondary
  "--color-table-header":         "18 18 18",     // #121212 — Figma sidebar + table bg
  "--color-tableBlueColor":       "51 153 255",   // #3399FF — Link / Hyperlink color
  "--color-tabActive":            "18 18 18",     // #121212 — Figma sidebar + table bg
  "--color-activeBorder":         "0 121 194",    // #0079C2 — Figma Brand Primary Blue
  "--color-disabled":             "112 112 112",  // #707070 — Figma Text Muted / Disabled Icon
  "--color-dropdown-bg":          "28 28 28",     // #1C1C1C — Figma Dark Mode Dropdown/Selected Background
};

/** Saved tenant values per CSS variable, captured AFTER the tenant
 * ThemeProvider has written its inline styles, so we can accurately
 * restore them when the user switches back to light mode.
 * Lives at module scope so it survives provider re-renders. */
const savedTenantTokens: Record<string, string> = {};

/** Apply or remove the dark inline overrides on `<html>`.
 *
 * @param theme          - The resolved appearance to apply.
 * @param captureTenant  - When true, re-capture the current inline
 *   values as the "light" baseline before overwriting them.  Pass
 *   true only from the rAF callback (after tenant ThemeProvider has
 *   already written its values) so the saved baseline is accurate.
 */
const applyAppearanceTokens = (
  theme: ResolvedAppearance,
  captureTenant = false,
): void => {
  const root = document.documentElement;
  if (theme === "dark") {
    Object.entries(DARK_TOKENS).forEach(([cssVar, value]) => {
      // Only capture the tenant's light-mode baseline if:
      // 1. The key hasn't been saved yet (first dark-mode activation), OR
      // 2. captureTenant is true AND the current inline value is NOT a dark
      //    token value (meaning tenant ThemeProvider just re-wrote it).
      // We never re-capture once dark values are already written inline —
      // that would overwrite the saved light baseline with dark values,
      // making light-mode restore impossible.
      const currentInline = root.style.getPropertyValue(cssVar);
      const isDarkValue = currentInline.trim() === value.trim();
      if (!(cssVar in savedTenantTokens) || (captureTenant && !isDarkValue)) {
        savedTenantTokens[cssVar] = currentInline;
      }
      root.style.setProperty(cssVar, value);
    });
  } else {
    // Restore each tenant value (or remove the inline override entirely
    // if the tenant didn't have one).
    Object.keys(DARK_TOKENS).forEach((cssVar) => {
      const original = savedTenantTokens[cssVar];
      if (original) {
        root.style.setProperty(cssVar, original);
      } else {
        root.style.removeProperty(cssVar);
      }
    });
  }
};

interface AppearanceContextValue {
  /** The user's chosen preference (raw, may be "system"). */
  appearance: Appearance;
  /** The currently applied theme (always "light" or "dark"). */
  resolvedAppearance: ResolvedAppearance;
  /** Update the preference and persist it. */
  setAppearance: (next: Appearance) => void;
}

/**
 * ECOPD-18134 — the preference is saved PER USER within the browser (PO decision:
 * "Option 1"), so the next person to sign in on the same browser does not inherit it.
 *
 *   appearance:<tenantId>:<userId>  the user's own choice
 *   appearance:lastUser             key of the signed-in user, read only while the
 *                                   session is restoring on a reload (no flash of the
 *                                   default theme); removed as soon as nobody is signed in
 *   appearance                      the old shared key, removed and never read: it may
 *                                   belong to a different user
 *
 * Signed out (login page) the default applies and nothing is saved. The choice does not
 * follow the user to another browser or device.
 */
const LEGACY_STORAGE_KEY = "appearance";
const LAST_USER_KEY = "appearance:lastUser";
const DEFAULT_APPEARANCE: Appearance = "system";

const userStorageKey = (tenantId: string, userId: string): string =>
  `appearance:${tenantId}:${userId}`;

const AppearanceContext = createContext<AppearanceContextValue | undefined>(
  undefined,
);

const isAppearance = (v: unknown): v is Appearance =>
  v === "light" || v === "dark" || v === "system";

const storageGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const storageSet = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — fall back to in-memory only */
  }
};

const storageRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing to remove */
  }
};

/** The saved preference under `storageKey`, or the default when there is none. */
const readSavedAppearance = (storageKey: string | null): Appearance => {
  if (typeof window === "undefined" || !storageKey) return DEFAULT_APPEARANCE;
  const v = storageGet(storageKey);
  return isAppearance(v) ? v : DEFAULT_APPEARANCE;
};

/** First render: the last signed-in user's preference, while the session restores. */
const readInitialAppearance = (): Appearance => {
  if (typeof window === "undefined") return DEFAULT_APPEARANCE;
  return readSavedAppearance(storageGet(LAST_USER_KEY));
};

const getSystemAppearance = (): ResolvedAppearance => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const resolve = (appearance: Appearance): ResolvedAppearance =>
  appearance === "system" ? getSystemAppearance() : appearance;

interface AppearanceProviderProps {
  children: ReactNode;
}

/**
 * Tracks the user's light / dark / system preference, persists it to
 * localStorage per signed-in user, and toggles a `dark` class on `<html>` so the existing
 * CSS-variable theme tokens (in globals.css `.dark` ruleset) take
 * effect across the app.
 *
 * This lives ALONGSIDE the tenant `ThemeProvider`. Tenant branding sets
 * primary / secondary colors per customer; appearance flips the
 * background / surface / text tokens between light and dark variants.
 */
export const AppearanceProvider: React.FC<AppearanceProviderProps> = ({
  children,
}) => {
  const [appearance, setAppearanceState] = useState<Appearance>(
    readInitialAppearance,
  );
  const [resolvedAppearance, setResolvedAppearance] =
    useState<ResolvedAppearance>(() => resolve(readInitialAppearance()));

  // Whose preference applies: the signed-in user's own key, or none (default).
  const { user, loading: authLoading } = useAuth();
  const userKey = user?.id ? userStorageKey(user.tenantId, user.id) : null;

  // The old shared key could hold another user's choice — drop it, never read it.
  useEffect(() => {
    storageRemove(LEGACY_STORAGE_KEY);
  }, []);

  // Switch to the signed-in user's preference once the session is known; signed
  // out (login page, expired session, after logout) falls back to the default.
  useEffect(() => {
    if (authLoading) return;
    if (userKey) {
      storageSet(LAST_USER_KEY, userKey);
    } else {
      storageRemove(LAST_USER_KEY);
    }
    setAppearanceState(readSavedAppearance(userKey));
  }, [authLoading, userKey]);

  // Watch the tenant theme so we re-apply dark tokens any time the
  // tenant `applyTheme` rewrites the inline styles on `<html>`.
  const { theme: tenantTheme } = useThemeContext();

  // Apply the resolved theme to <html>:
  //  • toggle the `dark` class (so any `dark:*` Tailwind variants work)
  //  • apply / restore the dark CSS variables as INLINE styles (to win
  //    over the tenant ThemeProvider's inline light-theme styles)
  //  • set `color-scheme` so native UI (scrollbars, form inputs, etc.)
  //    uses the right built-in palette
  //
  // Tenant `ThemeProvider` runs `applyTheme` in its own useEffect; on
  // initial mount React fires CHILD effects before PARENT, which means
  // our effect runs FIRST (we're the child) and tenant overwrites our
  // tokens immediately after. To beat this race we also re-apply on
  // the next animation frame — by then the tenant has already written
  // its values, and our re-application wins. Cheap, runs once on mount
  // and again only when our deps actually change.
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedAppearance === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    root.style.colorScheme = resolvedAppearance;
    applyAppearanceTokens(resolvedAppearance);
    // Re-apply on the next frame so we run AFTER tenant ThemeProvider's
    // useEffect (which fires later because it's a parent of ours).
    // Pass captureTenant=true so we snapshot the tenant's inline values
    // AFTER they have been written — giving us an accurate light-mode
    // baseline to restore when the user switches back to light.
    const rafId = requestAnimationFrame(() => {
      applyAppearanceTokens(resolvedAppearance, true);
    });
    return () => cancelAnimationFrame(rafId);
  }, [resolvedAppearance, tenantTheme]);

  // Re-resolve when appearance changes.
  useEffect(() => {
    setResolvedAppearance(resolve(appearance));
  }, [appearance]);

  // While appearance === "system", listen for OS-level changes so the
  // app flips theme without a reload.
  useEffect(() => {
    if (appearance !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent): void => {
      setResolvedAppearance(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [appearance]);

  // Saved only for a signed-in user; on the login page the change is in-memory.
  const setAppearance = useCallback((next: Appearance): void => {
    setAppearanceState(next);
    if (userKey) storageSet(userKey, next);
  }, [userKey]);

  const value = useMemo<AppearanceContextValue>(
    () => ({ appearance, resolvedAppearance, setAppearance }),
    [appearance, resolvedAppearance, setAppearance],
  );

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = (): AppearanceContextValue => {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error("useAppearance must be used within an AppearanceProvider");
  }
  return ctx;
};